import React, { useEffect, useMemo } from 'react';
import { Card, Box, Typography, Avatar, Skeleton } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { getMilestoneAchiever } from '../../../../../utils/constants/pointColors';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getMilestoneAchievers } from '../../../../../redux/features/poinTrackerSlice';

export default function MilestoneAchieversCard({ fromDate, toDate, selectedTeam }) {
  const dispatch = useDispatch();
  const { loading, responseData } = useSelector((state) => state.pointTracker.milestoneAchievers);

  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      wingId: selectedTeam?.id === -1 ? null : selectedTeam?.id,
    };
  }, [fromDate, toDate, selectedTeam?.id]);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(getMilestoneAchievers(dispatchParams));
    }
  }, [dispatch, dispatchParams]);

  const achievers = useMemo(() => {
    const data = responseData?.data?.lastMonth || [];

    return data.map((item, index) => ({
      rank: index + 1,
      name: item.name,
      admissions: item.admissions,
      team: item.team,
    }));
  }, [responseData]);

  return (
    <Card
      sx={{
        width: '100%',
        p: 3,
        borderRadius: 4,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        bgcolor: '#ffffff',
      }}
    >
      {/* Title */}
      {loading ? (
        <Skeleton width="70%" height={30} sx={{ mb: 2 }} />
      ) : (
        <Typography variant="h6" fontWeight="800" sx={{ mb: 2 }}>
          Last Month <span style={{ color: '#9c27b0' }}>Milestone Achievers</span>
        </Typography>
      )}

      {/* Loading Skeleton */}
      {loading ? (
        [...Array(3)].map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2,
              mb: 2,
              borderRadius: 3,
              bgcolor: '#f8faff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton width={20} />
              <Skeleton variant="circular" width={45} height={45} />
              <Box>
                <Skeleton width={120} height={20} />
                <Skeleton width={100} height={15} />
              </Box>
            </Box>

            <Box sx={{ textAlign: 'right' }}>
              <Skeleton variant="circular" width={30} height={30} />
              <Skeleton width={60} height={15} sx={{ mt: 1 }} />
            </Box>
          </Box>
        ))
      ) : achievers.length === 0 ? (
        // ✅ No Data
        <Typography align="center" color="text.secondary">
          No achievers found
        </Typography>
      ) : (
        // ✅ Actual Data
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {achievers.map((person) => {
            const { color } = getMilestoneAchiever(person.rank);

            return (
              <Box
                key={person.rank}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: 3,
                  bgcolor: '#f8faff',
                  border: '1px solid #eef2f8',
                }}
              >
                {/* Left */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" fontWeight="bold" color="text.secondary">
                    {person.rank}.
                  </Typography>

                  <Avatar
                    sx={{
                      width: 45,
                      height: 45,
                      background: 'linear-gradient(135deg, #474953, #8f8788)',
                    }}
                  />

                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {person.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {person.admissions} Admissions • {person.team}
                    </Typography>
                  </Box>
                </Box>

                {/* Right */}
                <Box sx={{ textAlign: 'right' }}>
                  <EmojiEventsIcon sx={{ color: color, fontSize: 32 }} />
                  <Box
                    sx={{
                      mt: 0.5,
                      px: 1,
                      py: 0.2,
                      bgcolor: '#dbeafe',
                      color: '#2563eb',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                    }}
                  >
                    Admission
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Card>
  );
}
